import { useCallback } from 'react';

import type {
  ApiFrontendConfigurationRetrieve200,
  ApiFrontendConfigurationRetrieve200Flags,
} from '../../../apiClient/latest/model';
import { useApiFrontendConfigurationRetrieve } from '../../../apiClient/latest/api-frontend-configuration-retrieve';
import { useApiI18nSetLanguageCreate } from '../../../apiClient/latest/api-i18n-set-language-create';
import type { ApiFrontendConfigurationRetrieveParams } from '../../../apiClient/latest/model';
import { FRONTEND_CONFIGURATION_FLAGS } from '../../../public-configs';
import { Logger } from '../../Logging';
import { ErrorType } from '../../Network/orvalHTTPInterface';

export type SetLanguage = (data: { language: string }) => void;

// convenience export with smaller varname
export const FLAGS = FRONTEND_CONFIGURATION_FLAGS;

export type Flag = (typeof FLAGS)[keyof typeof FLAGS];

export type IsFlagActive = (flag: Flag, defaultValue?: boolean) => boolean;

export type FrontendConfigurationFlagNames = keyof typeof FRONTEND_CONFIGURATION_FLAGS;

export type UseFrontendConfiguration = ReturnType<typeof useApiFrontendConfigurationRetrieve> & {
  data?: ApiFrontendConfigurationRetrieve200; // seems to be required to type this again?
  error: ErrorType<Error> | null;
  isFlagActive: IsFlagActive;
  language?: string;
  settings?: ApiFrontendConfigurationRetrieve200['settings'];
  supportsReferral?: boolean;
  setLanguage: SetLanguage;
  branchKey?: string;
  supportsMobileApp?: boolean;
};

/**
 *
 * This function is intended to be used in a context where we cannot use React hooks.
 * Opt to use `useFrontendConfiguration().isFlagActive` if you are in a React context.
 */
export const isFlagActiveForConfig = (
  hashedFlags: ApiFrontendConfigurationRetrieve200Flags,
  flag: string,
  defaultValue = false
) => {
  if (!hashedFlags) {
    Logger.warn(
      `No flags found in frontend configuration data, using default value of ${defaultValue}`
    );
    return defaultValue;
  }
  if (hashedFlags[flag] === undefined) {
    // warn about slightly weird legacy behavior where we return false instead of default value
    Logger.warn(
      `Flag ${flag} was not found in the frontend configuration data, this will cause isFlagActive to return false instead of using default value of ${defaultValue}`
    );
  }
  return !!hashedFlags[flag];
};

export const useFrontendConfiguration = ({
  cacheTime = 5 * 60 * 1000,
  enabled = true,
  refetchOnMount = false,
  params,
}: Partial<{
  params?: ApiFrontendConfigurationRetrieveParams;
  cacheTime: number;
  enabled: boolean;
  refetchOnMount: boolean | 'always';
}> = {}): UseFrontendConfiguration => {
  const query = useApiFrontendConfigurationRetrieve(params, {
    query: { enabled, refetchOnMount, cacheTime },
  });

  const mutation = useApiI18nSetLanguageCreate({
    mutation: {
      onSuccess() {
        window?.location.reload();
      },
    },
  });
  const setLanguage: SetLanguage = useCallback(async (data) => {
    mutation.mutate({ data });
  }, []);

  const { flags: hashedFlags } = query.data || {};

  const settings = query.data?.settings;
  const language = settings?.language;
  const supportsReferral = settings?.supportsReferral;
  const branchKey = settings?.branchKey;
  const supportsMobileApp = settings?.supportsMobileApp;

  return {
    ...query,
    isFlagActive: (flag, defaultValue = false) =>
      isFlagActiveForConfig(hashedFlags || {}, flag, defaultValue),
    settings,
    language,
    supportsReferral,
    setLanguage,
    branchKey,
    supportsMobileApp,
  };
};
